import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Reply, ShieldQuestion, FileText, Lightbulb, GitFork, MessageSquare } from "lucide-react";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/cn";
import { timeAgo } from "@/lib/vocabulary";
import { Avatar } from "@/components/AppShell";
import { Button } from "@/components/Button";
import type { ID } from "@/types";

const TYPE_META: Record<string, { label: string; icon: typeof MessageSquare; tone: string }> = {
  comment: { label: "comment", icon: MessageSquare, tone: "muted" },
  evidence_request: { label: "evidence request", icon: FileText, tone: "warn" },
  rewording: { label: "rewording", icon: Lightbulb, tone: "info" },
  challenge: { label: "challenge", icon: ShieldQuestion, tone: "danger" },
  clarification: { label: "clarification", icon: MessageSquare, tone: "info" },
  bridge: { label: "bridge", icon: GitFork, tone: "primary" },
};

export function CommentThread({
  strainId, nodeId, edgeId,
}: { strainId: ID; nodeId?: ID; edgeId?: ID }) {
  const comments = useStore((s) => s.comments);
  const addComment = useStore((s) => s.addComment);
  const [body, setBody] = useState("");
  const [type, setType] = useState<string>("comment");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState("");

  // anchored comments only — no giant detached comment section
  const anchored = comments.filter(
    (c) => c.strain_id === strainId
      && (nodeId ? c.node_id === nodeId : edgeId ? c.edge_id === edgeId : !c.node_id && !c.edge_id),
  );

  const submit = () => {
    if (!body.trim()) return;
    addComment({
      strain_id: strainId,
      node_id: nodeId,
      edge_id: edgeId,
      comment_type: type as any,
      body: body.trim(),
      parent_comment_id: null,
    });
    setBody("");
  };

  const submitReply = (parentId: ID) => {
    if (!replyBody.trim()) return;
    addComment({
      strain_id: strainId,
      node_id: nodeId,
      edge_id: edgeId,
      comment_type: "comment",
      body: replyBody.trim(),
      parent_comment_id: parentId,
    });
    setReplyBody("");
    setReplyTo(null);
  };

  return (
    <div className="pt-2 border-t border-[var(--color-border)]">
      <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-2 flex items-center gap-1.5">
        <MessageSquare size={11} /> comments ({anchored.length})
      </div>

      <div className="space-y-2.5">
        <AnimatePresence initial={false}>
          {anchored.map((c) => {
            const tm = TYPE_META[c.comment_type] ?? TYPE_META.comment;
            const TIcon = tm.icon;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-2.5"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <Avatar initials={c.author_avatar ?? c.author.slice(0, 2).toUpperCase()} size={20} />
                  <span className="text-[12px] font-medium text-[var(--color-ink)]">{c.author}</span>
                  <span className={cn("inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded", `text-[var(--color-ink-muted)]`)}>
                    <TIcon size={9} /> {tm.label}
                  </span>
                  <span className="text-[10px] text-[var(--color-ink-faint)] ml-auto">{timeAgo(c.created_at)}</span>
                </div>
                <p className="text-[13px] text-[var(--color-ink-soft)] leading-relaxed ml-7 -mt-1">{c.body}</p>
                <div className="ml-7 mt-1.5">
                  <button
                    onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
                    className="inline-flex items-center gap-1 text-[10px] text-[var(--color-ink-faint)] hover:text-[var(--color-ink-muted)]"
                  >
                    <Reply size={10} /> reply
                  </button>
                </div>

                <AnimatePresence>
                  {replyTo === c.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-7 mt-2"
                    >
                      <textarea
                        autoFocus
                        value={replyBody}
                        onChange={(e) => setReplyBody(e.target.value)}
                        rows={2}
                        placeholder="Your reply…"
                        className="w-full px-2.5 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[13px] text-[var(--color-ink)] resize-none"
                      />
                      <div className="flex justify-end gap-1.5 mt-1.5">
                        <Button size="xs" variant="ghost" onClick={() => { setReplyTo(null); setReplyBody(""); }}>Cancel</Button>
                        <Button size="xs" onClick={() => submitReply(c.id)}>Reply</Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {anchored.length === 0 && (
          <div className="text-[12px] text-[var(--color-ink-faint)] italic py-2">
            No comments anchored here yet.
          </div>
        )}
      </div>

      {/* composer */}
      <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)]/50 p-2.5">
        <div className="flex items-center gap-1 mb-2">
          {Object.entries(TYPE_META).slice(0, 5).map(([v, m]) => (
            <button
              key={v}
              onClick={() => setType(v)}
              className={cn(
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider transition-colors",
                type === v
                  ? "bg-[var(--color-surface-3)] text-[var(--color-ink)]"
                  : "text-[var(--color-ink-faint)] hover:text-[var(--color-ink-muted)]",
              )}
            >
              <m.icon size={9} /> {m.label}
            </button>
          ))}
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder={nodeId ? "Comment on this node…" : edgeId ? "Comment on this connection…" : "Comment…"}
          className="w-full bg-transparent text-[13px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] outline-none resize-none"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
        />
        <div className="flex justify-end mt-1">
          <Button size="xs" onClick={submit} disabled={!body.trim()}>Comment</Button>
        </div>
      </div>
    </div>
  );
}
